<template lang="pug">
  .study
    v-toolbar.white(card, prominent)
      v-toolbar-title.light-blue--text.subheading Estudio
      v-spacer
      v-toolbar-title.subheading.protocol-counter Protocolo {{ `#${protocol}` }}
      v-btn(icon, v-if='isEditing', @click='print(study._id)')
        v-icon print
      v-dialog(v-if='isEditing', v-model='displayEmailPrompt', width='400')
        v-btn(icon, slot='activator')
          v-icon email
        v-card
          v-card-title: span.title Enviar por correo
          v-card-text
            v-text-field(
              label='E-mail'
              type='email'
              hint='Dirección de correo del destinatario'
              @keyup.enter='sendStudy(study)'
              @input='validEmail = true'
              required
              v-model='study.patient.email'
              :rules='[validEmail]'
            )
            v-checkbox(
              color='primary'
              label='Recordar correo para este paciente'
              v-model='updatePatientEmail'
            )
          v-card-actions
            v-spacer
            v-btn.blue-grey--text(
              flat
              @click='displayEmailPrompt = false'
            ) Cancelar
            v-btn.pink--text(
              :loading='sendingEmail'
              flat
              @click='sendStudy(study)'
            ) Enviar

    .study__basic-info
      .study__basic-info__field
        v-select(
          autocomplete
          return-object
          cache-items
          required
          label='Paciente'
          v-model='study.patient'
          item-text='label'
          :async-loading='loading'
          :search-input.sync='patientSearchQuery'
          :items='patients'
          :debounce-search='500'
        )
          template(slot='item', scope='data')
            .item
              .item__title {{ data.item.first_name }} {{ data.item.last_name }}
              .item__description {{ data.item.dni }}

          template(slot='selection', scope='data')
            .item
              span {{ data.item.first_name }}
        v-progress-circular.primary--text(indeterminate, v-if='loading', size='24')
      .study__basic-info__field
        v-text-field(
          label='Medico derivante'
          v-model='study.medic'
          required
        )
      .study__basic-info__field
        v-text-field(
          label='Obra Social'
          :disabled='!study.patient.value'
          v-model='study.patient.medicare'
        )
      .study__basic-info__field
        v-menu(
          lazy
          z-index='1000'
        )
          v-text-field(
            slot='activator'
            label='Fecha de extracción'
            v-model='formattedDate'
            prepend-icon='event'
            readonly
            required
          )
          v-date-picker(
            landscape
            locale='es-AR'
            v-model='studyDate'
            :date-format='formatDate'
            :formatted-value.sync='formattedDate'
          )
      .study__basic-info__field
        v-checkbox(
          color='primary'
          label='Falta orden/Particular no pago'
          v-model='study.noOrder'
        )
      .study__basic-info__field.study__basic-info__field--full-width
        v-text-field(
          label='Observaciones'
          v-model='study.comment'
          multi-line
          auto-grow
        )
    h5 Estudios
    .studies
      .studies__study(
        v-for='type, key of studyTypes'
        :class='{ "studies__study--checked": study.studies[key] }'
      )
        v-checkbox(
          color='primary'
          v-model='study.studies[key]'
          :label='type.label'
        )

    .results(v-if='isEditing')
      h5 Resultados
      v-tabs(
        v-model='currentForm'
        centered
      )
        v-tabs-bar.white
          v-tabs-item(
            v-for='value, key in study.studies'
            v-if='value'
            @click='setForm(key)'
            :href='key'
            :key='key'
            ripple
          ) {{ formSchemas[key].label }}
          v-tabs-slider.primary
        v-tabs-items
          v-tabs-content(
            v-for='result in study.results'
            v-if='result'
            :key='result.type'
            :id='result.type'
          )
            study-form(:form='formSchemas[result.type]', :values='result.data')
    v-card-actions
      v-btn.blue-grey--text(flat, @click='cancel') Cancelar
      v-btn.pink.lighten-2.submit-button(
        dark
        :disabled='!requestedStudies'
        @click='save'
      ) Guardar
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import moment from 'moment';
import findIndex from 'lodash.findindex';
import studyTypes from '@/mixins/study-types';
import studyPrint from '@/mixins/study-print';
import studySend from '@/mixins/study-send';
import studyForm from '@/components/study-form';
import api from '@/api';

// Schemas
import anemia from '@/form-schemas/anemia.json';
import anticoagulation from '@/form-schemas/anticoagulation.json';
import compatibility from '@/form-schemas/compatibility.json';
import hemostasis from '@/form-schemas/hemostasis.json';
import myelogram from '@/form-schemas/myelogram.json';
import thrombophilia from '@/form-schemas/thrombophilia.json';

const resultHasId = result => result && result._id; // eslint-disable-line


export default {
  name: 'study',

  beforeRouteLeave(from, to, next) {
    this.clearStudy();
    next();
  },

  data() {
    return {
      study: {
        patient: {},
        results: [],
        studies: {},
      },
      nextProtocol: null,
      patients: [],
      formattedDate: null,
      patientSearchQuery: null,
      currentForm: null,
      studyDate: null,
      formSchemas: {
        anemia,
        anticoagulation,
        compatibility,
        hemostasis,
        myelogram,
        thrombophilia,
      },
    };
  },

  async created() {
    const { seq } = await api.service('counter').get('studyId');
    this.nextProtocol = seq;
    this.init(this.$route);
  },

  components: {
    studyForm,
  },

  computed: {
    ...mapState('patient', {
      loading: 'isFindPending',
    }),

    isEditing() {
      return !!this.study._id; // eslint-disable-line
    },

    protocol() {
      return this.study.protocol ? this.study.protocol : this.nextProtocol;
    },

    requestedStudies() {
      const { studies } = this.study;

      return studies.anemia ||
        studies.anticoagulation ||
        studies.compatibility ||
        studies.hemostasis ||
        studies.myelogram ||
        studies.thrombophilia;
    },
  },

  methods: {
    ...mapMutations(['setPrintContent']),
    ...mapActions('study', {
      getStudy: 'get',
      createStudy: 'create',
      patchStudy: 'patch',
    }),

    ...mapActions('result', {
      createResult: 'create',
      patchResult: 'patch',
    }),

    ...mapActions('patient', {
      findPatient: 'find',
    }),

    ...mapMutations(['showSnack']),

    ...mapMutations('study', {
      clearStudy: 'clearCurrent',
    }),

    cancel() {
      this.$router.push({ name: 'StudyList' });
    },

    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },

    async searchPatients() {
      try {
        const query = { $search: this.patientSearchQuery };
        const { data } = await this.findPatient({ query });
        this.patients = [this.study.patient, ...data];
      } catch (e) {
        throw e;
      }
    },

    save() {
      if (JSON.stringify(this.study.patient) === '{}') {
        this.showSnack('No se seleccionó un paciente');
        throw new Error('No se seleccionó un paciente');
      }

      if (!this.requestedStudies) {
        this.showSnack('No se seleccionaron estudios');
        throw new Error('No se seleccionaron estudios');
      }

      if (this.isEditing) {
        const resultsToCreate = this.study.results
          .filter(result => !resultHasId(result));
        const resultsToUpdate = this.study.results
          .filter(result => resultHasId(result));

        Promise
          .all([
            ...resultsToUpdate
              .map((result) => {
                const { _id } = result;
                return this.patchResult([_id, result]);
              }),
            ...resultsToCreate
              .map(result => this.createResult(result)),
          ])
          .then((results) => {
            const { _id } = this.study;
            this.study.results = results;
            return this.patchStudy([_id, this.study]);
          })
          .then(() => {
            this.showSnack('Estudio guardado');
            this.$router.push({ name: 'StudyList' });
          })
          .catch((e) => {
            this.showSnack('Falló al guardar el estudio');
            throw e;
          });
      } else {
        this.createStudy(this.study)
          .then(() => {
            this.showSnack('Estudio creado');
            this.$router.push({ name: 'StudyList' });
          })
          .catch((e) => {
            this.showSnack('Falló al guardar el estudio');
            throw e;
          });
      }
    },

    setForm(form) {
      this.currentForm = form;
    },

    setFormValues(name) {
      const formValues = this.study.results
        .filter(form => form.type === name);

      return formValues[0] ? formValues[0].data : {};
    },

    async init(route) {
      const { id } = route.params;

      if (id) {
        try {
          this.study = await this.getStudy(id);
          this.formattedDate = moment(this.study.date).format('MMM DD, YYYY');
          this.patients.push(this.study.patient);

          this.studyDate = moment(this.study.date);

          Object
            .keys(this.study.studies)
            .forEach((study) => {
              this.studyTypes[study].selected = this.study.studies[study];
            });
          Object
            .keys(this.study.studies)
            .some(study => (this.study.studies[study] ? this.setForm(study) : null));
        } catch (e) {
          throw e;
        }
      } else {
        this.study = {
          patient: {},
          results: [],
          studies: {},
        };
      }
    },
  },

  mixins: [studyTypes, studyPrint, studySend],

  watch: {
    $route(to) {
      this.init(to);
    },

    patientSearchQuery(value) {
      if (value && value.length >= 3) {
        this.searchPatients();
      }
    },

    currentForm(type) {
      if (findIndex(this.study.results, { type }) < 0) {
        this.study.results.push({
          data: {},
          study: this.study._id, // eslint-disable-line
          type,
        });
      }

      this.study.results = this.study.results
        .filter(result => result !== null);
    },

    studyDate() {
      this.study.date = moment(this.studyDate);
    },
  },
};
</script>

<style lang="less">
@import '~@/stylesheets/modules/colors';

.study {
  padding: 0 2rem;

  h5 {
    color: @light-blue;
  }

  .toolbar {
    margin: 0 -2rem 2rem;
    width: ~"calc(100% + 4rem)";
  }

  &__basic-info {
    display: flex;
    flex-flow: row wrap;

    &__field {
      width: 50%;
      position: relative;

      &:nth-of-type(odd) {
        padding-right: 1rem;
      }

      &:nth-of-type(even) {
        padding-left: 1rem;
      }

      > .menu {
        width: 100%;
      }

      &--full-width {
        width: 100%;

        &:nth-of-type(odd), &:nth-of-type(even) {
          padding: 0;
        }
      }

      textarea {
        resize: none;
      }

      .progress-circular {
        position: absolute;
        right: 2.5rem;
        top: 1.25rem;
      }
    }
  }

  .card__actions {
    background-color: #FAFAFA;
    border-top: 1px solid #F2F2F2;
    justify-content: flex-end;
    margin: 0 -2rem;
    padding: 2rem;
  }

  .submit-button {
    &[disabled] {
      color: #CCC !important;
    }
  }
}

.studies {
  display: flex;
  flex-flow: row wrap;
  margin: 0 -.5em 2em;

  &__study {
    width: ~'calc(33.33% - 1em)';
    padding: 1rem;
    border: 1px solid rgba(0, 0, 0, .05);
    border-radius: .25em;
    margin: .5em;

    .input-group {
      padding: 0;

      &__details {
        display: none;
      }
    }

    &--checked {
      background-color: fade(@light-blue, 10%);
    }
  }
}

.tabs {
  &__bar {
    margin: 0;
    border-bottom: 1px solid #F2F2F2 !important;
  }

  &__item {
    outline: none;
  }
}

.item {
  display: flex;
  flex-direction: column;

  &__description {
    font-size: .75em;
  }
}

.protocol-counter {
  margin-right: 1.5rem;
  color: rgba(0, 0, 0, 0.25);
}
</style>



// WEBPACK FOOTER //
// src/components/study.vue